// bill_subsystem_v1 + customer_invoices_v1: modal showing invoices of a single customer
import React, { useState, useEffect } from 'react';
import API from '../api';

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(s) {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (e) { return s; }
}

function statusBadge(status) {
  var colors = {
    draft: { bg: '#f1f5f9', fg: '#475569', label: 'ร่าง' },
    sent: { bg: '#dbeafe', fg: '#1d4ed8', label: 'ส่งแล้ว' },
    viewed: { bg: '#e0e7ff', fg: '#4338ca', label: 'ลูกค้าดู' },
    paid: { bg: '#dcfce7', fg: '#15803d', label: 'จ่ายแล้ว' },
    overdue: { bg: '#fed7aa', fg: '#c2410c', label: 'เกินกำหนด' },
    cancelled: { bg: '#fee2e2', fg: '#b91c1c', label: 'ยกเลิก' },
  };
  var c = colors[status] || { bg: '#f1f5f9', fg: '#475569', label: status || '-' };
  return <span style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>{c.label}</span>;
}

export default function CustomerInvoicesModal(props) {
  const customerId = props.customerId;
  const customerName = props.customerName;
  const initialMonth = props.month || '';
  const onClose = props.onClose;

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterMonth, setFilterMonth] = useState(initialMonth);

  useEffect(function () {
    if (!customerId) return;
    setLoading(true);
    var url = '/api/customers/' + customerId + '/invoices';
    if (filterMonth) url += '?month=' + filterMonth;
    API.get(url)
      .then(function (r) { setInvoices(r.data || []); })
      .catch(function () { setError('โหลดบิลไม่สำเร็จ'); })
      .finally(function () { setLoading(false); });
  }, [customerId, filterMonth]);

  var totalAmount = invoices.reduce(function (s, i) { return s + Number(i.total_amount || 0); }, 0);
  var paidAmount = invoices.filter(function (i) { return i.status === 'paid'; }).reduce(function (s, i) { return s + Number(i.total_amount || 0); }, 0);
  var outstandingAmount = invoices.filter(function (i) { return i.status === 'sent' || i.status === 'viewed' || i.status === 'overdue'; }).reduce(function (s, i) { return s + Number(i.total_amount || 0); }, 0);

  return (
    <div onClick={function () { onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, zIndex: 100, overflowY: 'auto' }}>
      <div onClick={function (e) { e.stopPropagation(); }} style={{ background: 'white', borderRadius: 14, padding: 20, maxWidth: 700, width: '100%', margin: '20px auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0 }}>👤 {customerName || 'ลูกค้า'}</h3>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>รายการบิลของลูกค้ารายนี้</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: '#475569' }}>เลือกเดือน:</label>
          <input type="month" value={filterMonth} onChange={function (e) { setFilterMonth(e.target.value); }} style={{ padding: 6, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }} />
          {filterMonth && <button onClick={function () { setFilterMonth(''); }} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: 11 }}>ดูทั้งหมด</button>}
        </div>

        {/* Stats summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>รวม</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>฿{fmtMoney(totalAmount)}</div>
            <div style={{ fontSize: 10, color: '#94a3b8' }}>{invoices.length} บิล</div>
          </div>
          <div style={{ background: '#dcfce7', borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#15803d' }}>เก็บแล้ว</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#15803d' }}>฿{fmtMoney(paidAmount)}</div>
          </div>
          <div style={{ background: '#fed7aa', borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#c2410c' }}>รอเก็บ</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#c2410c' }}>฿{fmtMoney(outstandingAmount)}</div>
          </div>
        </div>

        {/* Invoices list */}
        {loading && <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>กำลังโหลด...</div>}
        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 10, borderRadius: 8, fontSize: 13 }}>⚠️ {error}</div>}
        {!loading && !error && invoices.length === 0 && (
          <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8', fontSize: 13 }}>ไม่พบบิล</div>
        )}
        {!loading && invoices.length > 0 && (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>เลขที่</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>วันที่</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>ยอด</th>
                  <th style={{ padding: 8, textAlign: 'center' }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(function (inv) {
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{inv.invoice_number}</td>
                      <td style={{ padding: 8 }}>{fmtDate(inv.issue_date)}</td>
                      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>฿{fmtMoney(inv.total_amount)}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>{statusBadge(inv.status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
